import type { Match, Player } from '@/api/types';
import { attendanceIntervals, attendanceToInterval, intervalLabel } from '@/lib/attendance';
import { NONE_VALUE, type PlayerDraft } from './categoryDraft';
import styles from './ergebnistipp.module.css';

/** Zuschauer-Dropdown für den ganzen Spieltag (270 Intervalle). */
export function AttendanceSelect({
  value,
  onChange,
  locked,
  actualAttendance,
}: {
  value: string;
  onChange: (interval: string) => void;
  locked: boolean;
  /** Tatsächliche Zuschauerzahl, falls vom Admin erfasst. */
  actualAttendance?: number | null;
}) {
  const intervals = attendanceIntervals();
  const actualInterval =
    actualAttendance != null ? attendanceToInterval(actualAttendance) : null;

  return (
    <div className={styles.categoryField}>
      <span className={styles.categoryLabel}>Zuschauer</span>
      <select
        className={styles.categorySelect}
        value={value}
        disabled={locked}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Zuschauer-Intervall"
      >
        <option value="">— wählen —</option>
        {intervals.map((iv) => (
          <option key={iv.index} value={String(iv.index)}>
            {iv.label}
          </option>
        ))}
      </select>
      {actualInterval != null && (
        <span className={styles.categoryActual}>
          Ist: {intervalLabel(actualInterval)} ({actualAttendance})
        </span>
      )}
    </div>
  );
}

/** Tor- bzw. Assist-Dropdown (Kader + „keiner") pro Spiel. */
export function PlayerSelect({
  label,
  value,
  players,
  onChange,
  locked,
  actualPlayerIds,
}: {
  label: string;
  value: string;
  players: Player[];
  onChange: (value: string) => void;
  locked: boolean;
  /** Ist-Spieler (IDs); null = vom Admin noch nicht bestätigt, [] = bestätigt: keiner. */
  actualPlayerIds?: number[] | null;
}) {
  const actualNames =
    actualPlayerIds && actualPlayerIds.length > 0
      ? actualPlayerIds
          .map((pid) => players.find((p) => p.id === pid)?.name ?? `#${pid}`)
          .join(', ')
      : actualPlayerIds
        ? 'keiner'
        : null;

  return (
    <div className={styles.categoryField}>
      <span className={styles.categoryLabel}>{label}</span>
      <select
        className={styles.categorySelect}
        value={value}
        disabled={locked}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
      >
        <option value="">— wählen —</option>
        <option value={NONE_VALUE}>keiner</option>
        {players.map((p) => (
          <option key={p.id} value={String(p.id)}>
            {p.name}
          </option>
        ))}
      </select>
      {actualNames != null && <span className={styles.categoryActual}>Ist: {actualNames}</span>}
    </div>
  );
}

/** Zeile mit Tor- + Assist-Dropdown unter einem Spiel. */
export function MatchCategoryRow({
  match,
  players,
  draft,
  locked,
  onChange,
}: {
  match: Match;
  players: Player[];
  draft: PlayerDraft;
  locked: boolean;
  onChange: (matchId: number, field: 'scorer' | 'assist', value: string) => void;
}) {
  return (
    <div className={styles.categoryRow}>
      <PlayerSelect
        label="Tor"
        value={draft.scorer}
        players={players}
        locked={locked}
        actualPlayerIds={match.scorer_player_ids}
        onChange={(v) => onChange(match.id, 'scorer', v)}
      />
      <PlayerSelect
        label="Assist"
        value={draft.assist}
        players={players}
        locked={locked}
        actualPlayerIds={match.assist_player_ids}
        onChange={(v) => onChange(match.id, 'assist', v)}
      />
    </div>
  );
}
